import { html, LitElement, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { WidgetStateFrontEnd } from 'apps/webcomponents/src/genericDashboard/gzGenericWidget/types';
import { FontSizes, FontWeights } from 'apps/webcomponents/src/types/common/types';
import { classMap } from 'lit/directives/class-map.js';
import { ButtonColor, ButtonSize } from '../../gzIconButton/types';
import { translateFn } from '../../../utils/translator.helper';
import { PhasrRenderIconSet } from '../../../phasrRecommendations/uiUtils/iconSet';
import { PhasrIcons } from '../../../phasrRecommendations/uiUtils/types';

@customElement('gz-widget-section-with-icons-and-percentage-item')
export class GzWidgetSectionWithIconsAndPercentageItemWebComponent extends LitElement {
  @property({ type: String, attribute: true, reflect: true })
  name: string;

  @property({ type: WidgetStateFrontEnd, attribute: true, reflect: true })
  state: WidgetStateFrontEnd;

  @property({ type: Number, attribute: true, reflect: true })
  percentageValue: number;

  @property({ type: Number, attribute: true, reflect: true })
  iconValue: number;

  // Note: Add text without translation (without translateFn) to the tooltipString attribute
  @property({ type: String, attribute: true, reflect: true })
  tooltipString: string = 'Device: {value}, User: {value}';

  private readonly EMPTY_STATE_VALUE_STRING = '- -';
  private readonly EMPTY_STATE_NAME_STRING = '...';
  private readonly EXCEED_LIMIT_STRING = '9999+';
  private readonly MAX_DISPLAYED_VALUE_LIMIT = 9999;

  static get styles() {
    return styles;
  }

  render(): TemplateResult {
    return html` <div class="row">${this.renderItemInfo()} ${this.renderIcons()} ${this.renderDisplayValue()}</div> `;
  }

  private renderItemInfo(): TemplateResult {
    const itemName =
      this.state === WidgetStateFrontEnd.IS_ERROR || this.state === WidgetStateFrontEnd.IS_EMPTY
        ? this.EMPTY_STATE_NAME_STRING
        : this.name;

    const itemNameClasses = {
      'item-info': true,
      'empty-item-name': itemName === this.EMPTY_STATE_NAME_STRING,
    };

    return html`<div class="${classMap(itemNameClasses)}">
      <gz-tooltip class="label-tooltip" isEllipsis tooltipText="${itemName}">
        <div class="item-name">${itemName}</div>
      </gz-tooltip>
    </div>`;
  }

  private renderDisplayValue(): TemplateResult {
    let displayValue =
      this.state === WidgetStateFrontEnd.IS_ACTIVE ? this.percentageValue + '%' : this.EMPTY_STATE_VALUE_STRING;
    if (this.percentageValue !== 0) {
      displayValue = '-' + displayValue;
    }

    const percentageClasses = {
      'percentage-value': true,
      'empty-value': displayValue === this.EMPTY_STATE_VALUE_STRING,
    };

    return html`<gz-text
      fontsize="${FontSizes.PARAGRAPH_12}"
      fontweight="${FontWeights.SEMIBOLD}"
      class="${classMap(percentageClasses)}"
    >${displayValue}</gz-text
    >`;
  }

  private renderIcons(): TemplateResult {
    const displayValue =
      this.state === WidgetStateFrontEnd.IS_ACTIVE ? this.getIconValue() : this.EMPTY_STATE_VALUE_STRING;

    const tooltipIconText = translateFn(this.tooltipString, {
      value: this.iconValue,
    });

    return html`
      <div class="d-flex align-items-center">
        <gz-tooltip class="d-flex align-items-center" tooltipText="${this.isExceedeLimit() ? tooltipIconText : ''}">
          ${PhasrRenderIconSet.createIcon(PhasrIcons.BEHAVIORAL_PROFILES_ICON, ButtonColor.SECONDARY, ButtonSize.SIZE_24, 'svg-icon')}
          <gz-text fontsize="${FontSizes.PARAGRAPH_12}" class="icon-value">${displayValue}</gz-text>
        </gz-tooltip>
      </div>`;
  }

  private getIconValue(): string | number {
    return this.isExceedeLimit() ? this.EXCEED_LIMIT_STRING : this.iconValue;
  }

  private isExceedeLimit(): boolean {
    return this.iconValue > this.MAX_DISPLAYED_VALUE_LIMIT;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-widget-section-with-icons-and-percentage-item': GzWidgetSectionWithIconsAndPercentageItemWebComponent;
  }
}
